import moment from 'moment'
import { get, uniqBy } from 'lodash'
import { DATE_FORMAT } from '@src/config/momentFormat'
import { Bugsnag } from '@src/config'
import Unleash from '@src/config/unleash'
import { Calendar } from './app.types'

const FLAG_INITIALIZATION_TIMEOUT_MS = 3000
let waitForFlagsInitializationPromise: Promise<boolean> | null = null

export const calculateRangeDays = (startDate: string, endDate: string) => {
  return moment(endDate).diff(moment(startDate), 'days') + 1
}

export const initCalendar = () => ({
  startDate: moment().format(DATE_FORMAT),
  endDate: moment().format(DATE_FORMAT),
  rangeDays: 1,
  calendarData: [],
})

export type DateRange = { startDate: string; endDate: string }

export const validateDateRange = (range: DateRange) => validateDateRangeBase(range)

const validateDateRangeBase = ({ startDate, endDate }: DateRange, amountOfDays = 120) => {
  const diff = moment(endDate).diff(moment(startDate), 'days')
  const error = (diff > amountOfDays && `Range must be ${amountOfDays} days or fewer!`) || ''

  return { valid: !error, error }
}

export const processDateRangePayload = (payload: DateRange) => {
  const { startDate, endDate } = payload
  const rangeDays = calculateRangeDays(startDate, endDate)
  return {
    ...payload,
    rangeDays,
  }
}

export const yesterdayDateRangePayload = () => {
  const yesterday = moment().subtract(1, 'day')
  return {
    startDate: yesterday.format(DATE_FORMAT),
    endDate: yesterday.format(DATE_FORMAT),
    rangeDays: 1,
  }
}

export const todayDateRangePayload = () => ({
  startDate: moment().format(DATE_FORMAT),
  endDate: moment().format(DATE_FORMAT),
  rangeDays: 1,
})

export const processCalendarData = (calendarSelector: any) => {
  return function* (response: any, { select }: any) {
    const calendar: Calendar = yield select(calendarSelector)
    const calendarData = uniqBy(
      [...get(response, 'calendar.dates', ...calendar.calendarData, [])],
      'date',
    )

    return {
      calendarData,
    }
  }
}

export const waitForFlagsInitialization = (
  timeoutMs = FLAG_INITIALIZATION_TIMEOUT_MS,
): Promise<boolean> => {
  if (Unleash.client.isReady()) {
    return Promise.resolve(true)
  }

  if (waitForFlagsInitializationPromise) {
    return waitForFlagsInitializationPromise
  }

  waitForFlagsInitializationPromise = new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const finalize = (isReady: boolean) => {
      Unleash.client.off('ready', onReady)
      Unleash.client.off('error', onError)
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      waitForFlagsInitializationPromise = null
      resolve(isReady)
    }

    const onReady = () => finalize(true)
    const onError = (error: unknown) => {
      Bugsnag.notify(error as string)
      finalize(false)
    }

    Unleash.client.on('ready', onReady)
    Unleash.client.on('error', onError)

    if (Unleash.client.isReady()) {
      finalize(true)
      return
    }

    timeoutId = setTimeout(() => finalize(false), timeoutMs)
  })

  return waitForFlagsInitializationPromise
}
